import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Separator } from '../../ui/separator';
import { Palette, RefreshCw } from 'lucide-react';

const SeoColorsSection = ({ seoData, setSeoData, resetAllColors }) => {
  const handleChange = (field) => (e) => {
    setSeoData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const setTextColor = (color) => setSeoData(prev => ({ ...prev, button_text_color: color }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Personnalisation des couleurs
          </span>
          <Button variant="outline" size="sm" onClick={resetAllColors} data-testid="reset-all-colors-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Réinitialiser
          </Button>
        </CardTitle>
        <CardDescription>
          Adaptez les couleurs du site à votre identité visuelle. Les changements sont appliqués immédiatement après sauvegarde.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Couleur primaire */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">Couleur primaire</Label>
          <p className="text-sm text-muted-foreground">
            Couleur principale du site (header, liens, accents). Utilisée pour les éléments importants.
          </p>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={seoData.primary_color}
              onChange={handleChange('primary_color')}
              className="w-16 h-12 rounded cursor-pointer border-2 border-gray-200"
              data-testid="primary-color-picker"
            />
            <Input
              value={seoData.primary_color}
              onChange={handleChange('primary_color')}
              className="w-32 font-mono"
              placeholder="#0F2744"
            />
            <div
              className="flex-1 h-12 rounded-lg flex items-center justify-center text-white font-medium"
              style={{ backgroundColor: seoData.primary_color }}
            >
              Aperçu couleur primaire
            </div>
          </div>
        </div>

        <Separator />

        {/* Couleur secondaire */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">Couleur secondaire</Label>
          <p className="text-sm text-muted-foreground">
            Couleur d'accent utilisée pour les éléments secondaires et les survols.
          </p>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={seoData.secondary_color}
              onChange={handleChange('secondary_color')}
              className="w-16 h-12 rounded cursor-pointer border-2 border-gray-200"
              data-testid="secondary-color-picker"
            />
            <Input
              value={seoData.secondary_color}
              onChange={handleChange('secondary_color')}
              className="w-32 font-mono"
              placeholder="#4C8F6E"
            />
            <div
              className="flex-1 h-12 rounded-lg flex items-center justify-center text-white font-medium"
              style={{ backgroundColor: seoData.secondary_color }}
            >
              Aperçu couleur secondaire
            </div>
          </div>
        </div>

        <Separator />

        {/* Couleur des boutons */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">Couleur des boutons</Label>
          <p className="text-sm text-muted-foreground">
            Couleur de fond des boutons principaux (CTA, actions importantes).
          </p>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={seoData.button_color}
              onChange={handleChange('button_color')}
              className="w-16 h-12 rounded cursor-pointer border-2 border-gray-200"
              data-testid="button-color-picker"
            />
            <Input
              value={seoData.button_color}
              onChange={handleChange('button_color')}
              className="w-32 font-mono"
              placeholder="#4C8F6E"
            />
            <Button
              className="flex-1"
              style={{
                backgroundColor: seoData.button_color,
                color: seoData.button_text_color
              }}
            >
              Aperçu du bouton
            </Button>
          </div>
        </div>

        <Separator />

        {/* Couleur texte des boutons */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">Couleur du texte des boutons</Label>
          <p className="text-sm text-muted-foreground">
            Couleur du texte à l'intérieur des boutons (blanc ou noir généralement).
          </p>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={seoData.button_text_color}
              onChange={handleChange('button_text_color')}
              className="w-16 h-12 rounded cursor-pointer border-2 border-gray-200"
              data-testid="button-text-color-picker"
            />
            <Input
              value={seoData.button_text_color}
              onChange={handleChange('button_text_color')}
              className="w-32 font-mono"
              placeholder="#FFFFFF"
            />
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setTextColor('#FFFFFF')}>
                Blanc
              </Button>
              <Button variant="outline" size="sm" onClick={() => setTextColor('#000000')}>
                Noir
              </Button>
            </div>
          </div>
        </div>

        {/* Aperçu global */}
        <div className="mt-6 p-6 border rounded-lg bg-gray-50">
          <Label className="text-sm text-muted-foreground mb-4 block">Aperçu de la palette :</Label>
          <div className="flex gap-4 items-center">
            <div className="flex flex-col items-center">
              <div
                className="w-20 h-20 rounded-lg shadow-md"
                style={{ backgroundColor: seoData.primary_color }}
              />
              <span className="text-xs mt-2 text-gray-600">Primaire</span>
            </div>
            <div className="flex flex-col items-center">
              <div
                className="w-20 h-20 rounded-lg shadow-md"
                style={{ backgroundColor: seoData.secondary_color }}
              />
              <span className="text-xs mt-2 text-gray-600">Secondaire</span>
            </div>
            <div className="flex flex-col items-center">
              <div
                className="w-20 h-20 rounded-lg shadow-md flex items-center justify-center font-medium"
                style={{
                  backgroundColor: seoData.button_color,
                  color: seoData.button_text_color
                }}
              >
                Bouton
              </div>
              <span className="text-xs mt-2 text-gray-600">Bouton</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SeoColorsSection;
